
import {
  Vue, Component, Prop, PropSync, Watch,
} from 'vue-property-decorator';
import {
  SavedDocumentDto,
  getDocumentLibrary,
  deleteFromLibrary,
  updateLibraryDocument,
} from '@/features/shipment/services/document-library.service';
import { DocumentTypeLabels } from '@/features/shipment/types/documentType';
import { showErrorAlertDialog } from '@/features/common/helpers';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';
import SidePanel from '@/features/common/components/SidePanel.vue';

type SortMode = 'newest' | 'oldest' | 'nameAsc' | 'nameDesc';

@Component({
  components: { SidePanel },
})
export default class SavedLibraryModal extends Vue {
  @PropSync('show', { required: true }) syncedShow!: boolean;

  @Prop({ default: () => [] }) documents!: SavedDocumentDto[];

  @Prop({ default: () => new Set() }) addedDocumentIds!: Set<string>;

  @Prop({ default: Number.POSITIVE_INFINITY }) maxAddable!: number;

  // eslint-disable-next-line global-require
  private sortIconSrc = require('@/assets/images/electronic-trade-documents/sort-icon.svg');

  private searchQuery = '';

  private sortMode: SortMode = 'newest';

  private sortedDocs: SavedDocumentDto[] = [];

  private isFetchingSort = false;

  private selectedIds: Set<string> = new Set();

  private isEditDialogOpen = false;

  private editingId: number | null = null;

  private editName = '';

  private editType = 'NotSet';

  private isSavingEdit = false;

  private deletingId: number | null = null;

  // eslint-disable-next-line class-methods-use-this
  get documentTypeOptions(): Array<{ text: string; value: string }> {
    return Object.entries(DocumentTypeLabels).map(([value, text]) => ({
      text,
      value,
    }));
  }

  @Watch('syncedShow')
  private onModalVisibilityChanged(isOpen: boolean): void {
    if (!isOpen) return;
    this.sortMode = 'newest';
    this.fetchSorted();
    this.$nextTick(() => {
      const searchInput = this.$el?.querySelector(
        '.saved-library-modal__search input',
      ) as HTMLInputElement | null;
      // eslint-disable-next-line no-unused-expressions
      searchInput?.focus();
    });
  }

  get filteredDocuments(): SavedDocumentDto[] {
    const docs = this.sortedDocs.length ? this.sortedDocs : this.documents;

    if (!this.searchQuery) return [...docs];

    const q = this.searchQuery.toLowerCase();
    return docs.filter(
      (d) => (d.originalFileName || d.name).toLowerCase().includes(q),
    );
  }

  get documentRows(): Array<{
    doc: SavedDocumentDto;
    id: string;
    displayName: string;
    typeLabel: string;
    isAdded: boolean;
    isLimitReached: boolean;
    isReadOnly: boolean;
    isSelected: boolean;
    title: string | undefined;
  }> {
    const limitHit = this.selectedIds.size >= this.maxAddable;
    return this.filteredDocuments.map((doc) => {
      const id = String(doc.id);
      const displayName = doc.originalFileName || doc.name;
      const dt = doc.documentType as string;
      const typeLabel = (dt && dt !== 'NotSet')
        ? (DocumentTypeLabels[dt as keyof typeof DocumentTypeLabels] || dt)
        : '';
      const isAdded = this.addedDocumentIds.has(id);
      const isSelected = this.selectedIds.has(id);
      const isLimitReached = !isAdded && limitHit && !isSelected;
      const isReadOnly = isAdded || isLimitReached;
      let title: string | undefined;
      if (isAdded) {
        title = this.$t('documents.alreadyAdded') as string;
      } else if (isLimitReached) {
        title = this.$t('documents.maxDocumentsReached') as string;
      }
      return {
        doc, id, displayName, typeLabel, isAdded, isLimitReached, isReadOnly, isSelected, title,
      };
    });
  }

  // eslint-disable-next-line class-methods-use-this
  get sortOptions(): Array<{ value: SortMode; labelKey: string }> {
    return [
      { value: 'newest', labelKey: 'documents.sortNewest' },
      { value: 'oldest', labelKey: 'documents.sortOldest' },
      { value: 'nameAsc', labelKey: 'documents.sortNameAsc' },
      { value: 'nameDesc', labelKey: 'documents.sortNameDesc' },
    ];
  }

  private setSortMode(mode: SortMode): void {
    this.sortMode = mode;
    this.fetchSorted();
  }

  private static readonly SORT_PARAMS:
   Record<SortMode, { orderBy: string; orderByDescending: boolean }> = {
     newest: { orderBy: 'lastUsedUtc', orderByDescending: true },
     oldest: { orderBy: 'lastUsedUtc', orderByDescending: false },
     nameAsc: { orderBy: 'name', orderByDescending: false },
     nameDesc: { orderBy: 'name', orderByDescending: true },
   };

  private async fetchSorted(): Promise<void> {
    this.isFetchingSort = true;
    try {
      this.sortedDocs = await getDocumentLibrary(
        SavedLibraryModal.SORT_PARAMS[this.sortMode],
      );
    } catch {
      this.sortedDocs = [...this.documents];
    } finally {
      this.isFetchingSort = false;
    }
  }

  private isDocAdded(id: string): boolean {
    return this.addedDocumentIds.has(id);
  }

  private toggleDocSelection(id: string): void {
    if (this.isDocAdded(id)) return;
    const next = new Set(this.selectedIds);
    if (next.has(id)) {
      next.delete(id);
    } else {
      if (next.size >= this.maxAddable) return;
      next.add(id);
    }
    this.selectedIds = next;
  }

  private openEditDialog(doc: SavedDocumentDto): void {
    this.editingId = doc.id;
    this.editName = doc.name;
    this.editType = doc.documentType || 'NotSet';
    this.isEditDialogOpen = true;
    this.$nextTick(() => {
      const ref = this.$refs.editNameInput as { focus?: () => void } | undefined;
      // eslint-disable-next-line no-unused-expressions
      ref?.focus?.();
    });
  }

  private closeEditDialog(): void {
    if (this.isSavingEdit) return;
    this.isEditDialogOpen = false;
    this.editingId = null;
    this.editName = '';
    this.editType = 'NotSet';
  }

  private async commitEdit(): Promise<void> {
    if (this.editingId == null) return;
    const trimmed = this.editName.trim();
    if (!trimmed) return;

    const doc = this.documents.find((d) => d.id === this.editingId);
    if (!doc) {
      this.closeEditDialog();
      return;
    }

    const currentType = doc.documentType || 'NotSet';
    if (trimmed === doc.name && this.editType === currentType) {
      this.closeEditDialog();
      return;
    }

    this.isSavingEdit = true;
    const result = await updateLibraryDocument(doc.id, {
      name: trimmed,
      documentType: this.editType,
    });
    this.isSavingEdit = false;

    if (!result) {
      showErrorAlertDialog(
        this.$t('documents.errorMessages.libraryUpdateFailed') as string
          || 'Failed to update document. Please try again.',
      );
      return;
    }

    this.$emit('library-updated', result);
    this.closeEditDialog();
    this.fetchSorted();
  }

  private requestDelete(doc: SavedDocumentDto): void {
    eventBus.$once(eventNames.CONFIRMATION_DIALOG, async () => {
      this.deletingId = doc.id;
      const success = await deleteFromLibrary(doc.id);
      this.deletingId = null;

      if (!success) {
        showErrorAlertDialog(
          this.$t('documents.errorMessages.libraryDeleteFailed') as string
            || 'Failed to delete document. Please try again.',
        );
        return;
      }

      // Deselect if it was selected
      if (this.selectedIds.has(String(doc.id))) {
        const next = new Set(this.selectedIds);
        next.delete(String(doc.id));
        this.selectedIds = next;
      }

      this.$emit('library-deleted', doc.id);
      this.fetchSorted();
    });

    this.$store.commit('toggleConfirmDialog', {
      show: true,
      content: this.$t('documents.libraryDeleteConfirm', { name: doc.originalFileName || doc.name }) as string,
      title: this.$t('alertDialog.areYouSure') as string,
      btnText: this.$t('buttons.delete') as string,
    });
  }

  private addToShipment(): void {
    const docs = this.sortedDocs.length ? this.sortedDocs : this.documents;
    const selectedDocs = docs
      .filter((d) => {
        const id = String(d.id);
        return this.selectedIds.has(id) && !this.isDocAdded(id);
      })
      .slice(0, this.maxAddable);
    this.$emit('add-documents', selectedDocs);
    this.close();
  }

  private close(): void {
    this.syncedShow = false;
    this.searchQuery = '';
    this.selectedIds = new Set();
    this.sortedDocs = [];
    this.closeEditDialog();
  }
}
